import React, { useState, useEffect } from 'react';

const UserFriendlyLoading = ({ timeout = 10000 }) => {
  const [showSlowLoading, setShowSlowLoading] = useState(false);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    // Show "taking longer than usual" message after 5 seconds
    const slowTimer = setTimeout(() => {
      setShowSlowLoading(true);
    }, 5000);

    // Show error/retry option after timeout
    const errorTimer = setTimeout(() => {
      setShowError(true);
    }, timeout);

    return () => {
      clearTimeout(slowTimer);
      clearTimeout(errorTimer);
    };
  }, [timeout]);

  const handleRetry = () => {
    window.location.reload();
  };

  if (showError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center bg-white rounded-2xl shadow-xl p-8">
          <div className="text-6xl mb-4">⏰</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Loading is Taking Too Long
          </h2>
          <p className="text-gray-600 mb-6">
            The application is taking longer than usual to load. This might be due to a slow connection or server issues.
          </p>
          <div className="space-y-3">
            <button
              onClick={handleRetry}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
            >
              🔄 Try Again
            </button>
            <p className="text-sm text-gray-500">
              If the problem persists, contact{' '}
              <a href="mailto:support@axel.com" className="text-blue-600 hover:text-blue-800">
                support@axel.com
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        {/* Loading Spinner */}
        <div className="mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4">
            <svg 
              className="animate-spin h-8 w-8 text-white" 
              xmlns="http://www.w3.org/2000/svg" 
              fill="none" 
              viewBox="0 0 24 24"
            >
              <circle 
                className="opacity-25" 
                cx="12" 
                cy="12" 
                r="10" 
                stroke="currentColor" 
                strokeWidth="4"
              ></circle>
              <path 
                className="opacity-75" 
                fill="currentColor" 
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Loading Axel Marketplace
          </h2>
          
          {showSlowLoading ? (
            <div className="space-y-4">
              <p className="text-amber-600 font-medium">
                This is taking longer than usual...
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  💡 This might be due to a slow connection or high traffic. Please wait a bit longer.
                </p>
              </div>
            </div>
          ) : (
            <p className="text-gray-600">
              Please wait while we load your application...
            </p>
          )}
        </div>

        {/* Loading Progress Dots */}
        <div className="flex justify-center space-x-2 mb-8">
          <div className="w-3 h-3 bg-blue-600 rounded-full animate-pulse"></div>
          <div className="w-3 h-3 bg-blue-600 rounded-full animate-pulse" style={{animationDelay: '0.2s'}}></div>
          <div className="w-3 h-3 bg-blue-600 rounded-full animate-pulse" style={{animationDelay: '0.4s'}}></div>
        </div>

        {/* Tips */}
        <div className="bg-white rounded-lg shadow-lg p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-2">
            💡 Did you know?
          </h3>
          <p className="text-xs text-gray-600">
            Axel Marketplace helps you find the best insurance solutions for your business needs.
          </p>
        </div>
      </div>
    </div>
  );
};

export default UserFriendlyLoading;
